import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import AddressBookPicker from '@/components/AddressBookPicker';
import ZipAutofillInput from '@/components/ZipAutofillInput';
import DeclaredValueAndCoverage from '@/components/DeclaredValueAndCoverage';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Leaf, Package, Shield, DollarSign, ArrowRight, 
  Loader2, CheckCircle, Truck, MapPin, Zap
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SENDLE_TIERS = [
  {
    id: 'economy',
    name: 'MultiSend Economy',
    maxValue: 10,
    description: 'For shipments valued under $10.00',
    baseRate: 3.99,
    features: ['Carbon neutral', 'Door-to-door', 'Basic tracking'],
    badge: 'Budget Friendly',
    badgeColor: 'bg-emerald-500'
  },
  {
    id: 'guaranteed',
    name: 'MultiSend Guaranteed',
    maxValue: 300,
    description: 'Fully guaranteed shipments up to $300.00',
    baseRate: 6.99,
    features: ['Carbon neutral', 'Full guarantee', 'Priority handling', 'Insurance included', 'Signature on delivery'],
    badge: 'Fully Guaranteed',
    badgeColor: 'bg-blue-500'
  }
];

const SendlePage = () => {
  const [loading, setLoading] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [quote, setQuote] = useState(null);
  const [booked, setBooked] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const { getAuthHeaders, token } = useAuth();

  const [formData, setFormData] = useState({
    // Sender
    sender_name: '',
    sender_address: '',
    sender_city: '',
    sender_state: '',
    sender_zip: '',
    sender_phone: '',
    
    // Recipient
    recipient_name: '',
    recipient_address: '',
    recipient_city: '',
    recipient_state: '',
    recipient_zip: '',
    recipient_phone: '',
    
    // Package
    description: '',
    weight: '',
    declared_value: '',
    
    // Options
    accept_terms: false
  });

  const getQuote = async () => {
    if (!selectedTier) {
      toast.error('Please select a shipping tier');
      return;
    }

    const declaredValue = parseFloat(formData.declared_value) || 0;
    const tier = SENDLE_TIERS.find(t => t.id === selectedTier);
    
    if (declaredValue > tier.maxValue) {
      toast.error(`${tier.name} is only for shipments up to $${tier.maxValue.toFixed(2)}`);
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/sendle/quote`, {
        tier: selectedTier,
        sender_zip: formData.sender_zip,
        recipient_zip: formData.recipient_zip,
        weight: parseFloat(formData.weight) || 1,
        declared_value: declaredValue
      }, {
        headers: getAuthHeaders()
      });
      
      setQuote(response.data);
      toast.success('Quote generated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to get quote');
    } finally {
      setLoading(false);
    }
  };

  const bookShipment = async () => {
    if (!formData.accept_terms) {
      toast.error('Please accept the terms and conditions');
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/sendle/book`, {
        ...formData,
        tier: selectedTier,
        quote_id: quote?.id,
        total_cost: quote?.total
      }, {
        headers: getAuthHeaders()
      });
      
      setBookingId(response.data.id);
      setBooked(true);
      toast.success('Sendle shipment booked!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to book shipment');
    } finally {
      setLoading(false);
    }
  };

  if (booked) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="sendle-success">
        <Card className="border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10">
          <CardContent className="pt-6 text-center">
            <div className="w-20 h-20 bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">Shipment Booked!</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              Your Sendle shipment has been confirmed.
            </p>
            <code className="block bg-white dark:bg-slate-900 p-4 rounded-lg font-mono text-sm mb-6">
              Booking ID: {bookingId}
            </code>
            
            <div className="flex items-center justify-center gap-2 p-3 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg mb-6">
              <Leaf className="w-5 h-5 text-emerald-600" />
              <span className="text-emerald-700 dark:text-emerald-300 font-medium">
                100% Carbon Neutral Delivery
              </span>
            </div>

            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => { setBooked(false); setQuote(null); setSelectedTier(null); }}>
                New Shipment
              </Button>
              <Button onClick={() => window.location.href = '/dashboard/shipments'}>
                View All Shipments
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-fade-in" data-testid="sendle-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
              <Leaf className="w-6 h-6 text-white" />
            </div>
            Sendle Shipping
          </h1>
          <p className="text-slate-500 mt-1">Low-cost, carbon neutral shipping</p>
        </div>
        <Badge className="bg-emerald-500 text-white">
          <Leaf className="w-3 h-3 mr-1" />
          Carbon Neutral
        </Badge>
      </div>

      {/* Tier Selection */}
      <div className="grid md:grid-cols-2 gap-6">
        {SENDLE_TIERS.map((tier) => (
          <Card 
            key={tier.id}
            className={`cursor-pointer transition-all hover:shadow-lg ${
              selectedTier === tier.id 
                ? 'ring-2 ring-emerald-500 border-emerald-500' 
                : 'hover:border-slate-300'
            }`}
            onClick={() => setSelectedTier(tier.id)}
            data-testid={`tier-${tier.id}`}
          >
            <CardHeader>
              <div className="flex items-start justify-between">
                <div>
                  <Badge className={`${tier.badgeColor} text-white mb-2`}>
                    {tier.badge}
                  </Badge>
                  <CardTitle className="text-xl">{tier.name}</CardTitle>
                  <CardDescription className="mt-1">{tier.description}</CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-emerald-600">${tier.baseRate}</p>
                  <p className="text-sm text-slate-500">starting</p>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {tier.features.map((feature, index) => (
                  <div key={index} className="flex items-center gap-2 text-sm">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-600 dark:text-slate-400">Max Value</span>
                  <span className="font-semibold">${tier.maxValue.toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Shipping Form */}
      {selectedTier && (
        <div className="space-y-6">
          <DeclaredValueAndCoverage
            declaredValue={parseFloat(formData.declared_value) || 0}
            onDeclaredChange={(v) => setFormData({ ...formData, declared_value: String(v) })}
            coverage={formData.coverage_plan || 'A'}
            onCoverageChange={(c) => setFormData({ ...formData, coverage_plan: c })}
          />
          {/* Sender */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-500" />
                Sender Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <AddressBookPicker
                token={token}
                type="ship_from"
                testid="sendle-from"
                label="Address Book · Sender"
                value={{ name: formData.sender_name, city: formData.sender_city, state: formData.sender_state, zip: formData.sender_zip }}
                onSelect={(a) => setFormData({
                  ...formData,
                  sender_name: a.name || formData.sender_name,
                  sender_phone: a.phone || formData.sender_phone,
                  sender_address: a.street1 || formData.sender_address,
                  sender_city: a.city || formData.sender_city,
                  sender_state: a.state || formData.sender_state,
                  sender_zip: (a.zip_code || a.zip || formData.sender_zip),
                })}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.sender_name}
                    onChange={(e) => setFormData({...formData, sender_name: e.target.value})}
                    placeholder="Full name"
                    data-testid="sender-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.sender_phone}
                    onChange={(e) => setFormData({...formData, sender_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input
                  value={formData.sender_address}
                  onChange={(e) => setFormData({...formData, sender_address: e.target.value})}
                  placeholder="Street address"
                  data-testid="sender-address"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.sender_city}
                    onChange={(e) => setFormData({...formData, sender_city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input
                    value={formData.sender_state}
                    onChange={(e) => setFormData({...formData, sender_state: e.target.value})}
                    placeholder="CA"
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP *</Label>
                  <ZipAutofillInput
                    value={formData.sender_zip}
                    onChange={(v) => setFormData({...formData, sender_zip: v})}
                    onResolve={(g) => setFormData((f) => ({
                      ...f,
                      sender_city: f.sender_city || g.city || '',
                      sender_state: f.sender_state || g.state_abbr || '',
                    }))}
                    placeholder="90001"
                    testid="sender-zip"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Recipient */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="w-5 h-5 text-blue-500" />
                Recipient Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <AddressBookPicker
                token={token}
                type="ship_to"
                testid="sendle-to"
                label="Address Book · Recipient"
                value={{ name: formData.recipient_name, city: formData.recipient_city, state: formData.recipient_state, zip: formData.recipient_zip }}
                onSelect={(a) => setFormData({
                  ...formData,
                  recipient_name: a.name || formData.recipient_name,
                  recipient_phone: a.phone || formData.recipient_phone,
                  recipient_address: a.street1 || formData.recipient_address,
                  recipient_city: a.city || formData.recipient_city,
                  recipient_state: a.state || formData.recipient_state,
                  recipient_zip: (a.zip_code || a.zip || formData.recipient_zip),
                })}
              />
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.recipient_name}
                    onChange={(e) => setFormData({...formData, recipient_name: e.target.value})}
                    placeholder="Full name"
                    data-testid="recipient-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formData.recipient_phone}
                    onChange={(e) => setFormData({...formData, recipient_phone: e.target.value})}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address *</Label>
                <Input
                  value={formData.recipient_address}
                  onChange={(e) => setFormData({...formData, recipient_address: e.target.value})}
                  placeholder="Street address"
                  data-testid="recipient-address"
                />
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>City *</Label>
                  <Input
                    value={formData.recipient_city}
                    onChange={(e) => setFormData({...formData, recipient_city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div className="space-y-2">
                  <Label>State *</Label>
                  <Input
                    value={formData.recipient_state}
                    onChange={(e) => setFormData({...formData, recipient_state: e.target.value})}
                    placeholder="NY"
                    maxLength={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>ZIP *</Label>
                  <ZipAutofillInput
                    value={formData.recipient_zip}
                    onChange={(v) => setFormData({...formData, recipient_zip: v})}
                    onResolve={(g) => setFormData((f) => ({
                      ...f,
                      recipient_city: f.recipient_city || g.city || '',
                      recipient_state: f.recipient_state || g.state_abbr || '',
                    }))}
                    placeholder="10001"
                    testid="recipient-zip"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Package */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5 text-purple-500" />
                Package Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="What are you shipping?"
                  data-testid="package-description"
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Weight (lbs) *</Label>
                  <Input
                    type="number"
                    value={formData.weight}
                    onChange={(e) => setFormData({...formData, weight: e.target.value})}
                    placeholder="1.0"
                    min="0.1"
                    step="0.1"
                    data-testid="package-weight"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Declared Value ($) *</Label>
                  <Input
                    type="number"
                    value={formData.declared_value}
                    onChange={(e) => setFormData({...formData, declared_value: e.target.value})}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    max={SENDLE_TIERS.find(t => t.id === selectedTier)?.maxValue}
                    data-testid="declared-value"
                  />
                  <p className="text-xs text-slate-500">
                    Max: ${SENDLE_TIERS.find(t => t.id === selectedTier)?.maxValue.toFixed(2)} for {SENDLE_TIERS.find(t => t.id === selectedTier)?.name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Get Quote */}
          {!quote && (
            <Button 
              onClick={getQuote} 
              className="w-full btn-press bg-emerald-500 hover:bg-emerald-600" 
              size="lg"
              disabled={loading}
              data-testid="get-quote-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                <>
                  Get Sendle Quote
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}

          {/* Quote & Booking */}
          {quote && (
            <div className="space-y-6">
              <Card className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-900/20 dark:to-green-900/20 border-emerald-200 dark:border-emerald-800">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-emerald-600" />
                    Your Sendle Quote
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Base Rate ({SENDLE_TIERS.find(t => t.id === selectedTier)?.name})</span>
                      <span className="tabular-nums">${quote.base_rate?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Weight Surcharge</span>
                      <span className="tabular-nums">${quote.weight_surcharge?.toFixed(2)}</span>
                    </div>
                    {quote.guarantee_fee > 0 && (
                      <div className="flex justify-between text-emerald-600">
                        <span>Full Guarantee Protection</span>
                        <span className="tabular-nums">${quote.guarantee_fee?.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span>Carbon Offset</span>
                      <span className="tabular-nums text-emerald-600">Included</span>
                    </div>
                    <div className="border-t pt-3 flex justify-between text-lg font-semibold">
                      <span>Total</span>
                      <span className="text-emerald-600 tabular-nums">${quote.total?.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-white/50 dark:bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <Truck className="w-4 h-4 text-emerald-500" />
                      <span>Estimated delivery: <strong>{quote.estimated_days} business days</strong></span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Terms */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="accept_terms"
                      checked={formData.accept_terms}
                      onCheckedChange={(checked) => setFormData({...formData, accept_terms: checked})}
                    />
                    <Label htmlFor="accept_terms" className="text-sm cursor-pointer leading-relaxed">
                      I accept the Sendle shipping terms and SelleSend coverage terms. I understand that 
                      {selectedTier === 'economy' 
                        ? ' Economy tier provides basic coverage for items under $10.00 value.'
                        : ' Guaranteed tier provides full coverage for items up to $300.00 value with signature confirmation.'
                      } Carbon neutral shipping is included at no extra cost.
                    </Label>
                  </div>
                </CardContent>
              </Card>

              <div className="flex gap-4">
                <Button variant="outline" onClick={() => setQuote(null)} className="flex-1">
                  Edit Details
                </Button>
                <Button 
                  onClick={bookShipment}
                  className="flex-1 btn-press bg-emerald-500 hover:bg-emerald-600"
                  disabled={loading || !formData.accept_terms}
                  data-testid="book-shipment-btn"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : (
                    <>
                      Book Sendle Shipment - ${quote.total?.toFixed(2)}
                      <Leaf className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SendlePage;
